import { useState, useEffect } from 'react';

import { dataService } from '@/services/data-service';
import type { Association } from '@/types/models';
import { calculateDistance } from '@/utils/distance';

export interface ClosestAssociationWithinRadius {
	association: Association;
	distance: number; // in miles
}

/**
 * Hook to find the closest association within a 5-mile radius of a given location
 * @param latitude The latitude to search from
 * @param longitude The longitude to search from
 * @returns Object containing the closest association and distance, or null if no association within radius
 */
export function useClosestAssociationWithinRadius(
	latitude?: number,
	longitude?: number,
): ClosestAssociationWithinRadius | null {
	const [closestAssociation, setClosestAssociation] = useState<ClosestAssociationWithinRadius | null>(null);

	useEffect(() => {
		let cancelled = false;

		// If location is not provided, we can't calculate distance
		if (!latitude || !longitude) {
			setClosestAssociation(null);
			return;
		}

		const fetchClosestAssociation = async () => {
			try {
				const response = await dataService.getAssociations();
				if (cancelled) return;

				const associations = response.data || [];

				// Filter associations that have location data
				const associationsWithLocation = associations.filter(
					(assoc) => (assoc.latitude || assoc.lat) && (assoc.longitude || assoc.lng),
				);

				if (associationsWithLocation.length === 0) {
					if (!cancelled) setClosestAssociation(null);
					return;
				}

				// Calculate distances and find the closest one within 5 miles
				let closest: ClosestAssociationWithinRadius | null = null;
				let minDistance = Infinity;
				const maxRadiusMiles = 5; // 5-mile radius

				for (const association of associationsWithLocation) {
					const assocLat = association.latitude || association.lat!;
					const assocLng = association.longitude || association.lng!;

					const distanceInMiles = calculateDistance(
						latitude,
						longitude,
						assocLat,
						assocLng,
					);

					// Only consider associations within 5 miles
					if (distanceInMiles <= maxRadiusMiles && distanceInMiles < minDistance) {
						minDistance = distanceInMiles;
						closest = { association, distance: distanceInMiles };
					}
				}

				if (!cancelled) setClosestAssociation(closest);
			} catch (error) {
				if (!cancelled) {
					console.error('Error fetching associations:', error);
					setClosestAssociation(null);
				}
			}
		};

		fetchClosestAssociation();

		return () => {
			cancelled = true;
		};
	}, [latitude, longitude]);

	return closestAssociation;
}
